import * as React from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, Receipt, AlertTriangle, CheckCircle2 } from "lucide-react";
import { parseBesiktasXml } from "@/features/besiktas/parseXml";
import { parseBesiktasPdf } from "@/features/besiktas/parsePdf";
import type { ParsedInvoice } from "@/features/besiktas/types";
import { isValidIsoDate } from "@/features/besiktas/common";
import { isValidVatAmount } from "@/features/besiktas/vatValidation";
import { useAuth } from "@/supabase/AuthContext";
import { createBrowserSupabase } from "@/supabase/client";
import { formatCurrencyAmount } from "@/lib/currency";
import { auditCompanyDataAfterChange } from "@/lib/ai";

const money = (value: number | null) => formatCurrencyAmount(value);

const BesiktasInvoices: React.FC = () => {
  const { user, companyId } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const [testOnly, setTestOnly] = React.useState(false);
  const [savedMessage, setSavedMessage] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [invoice, setInvoice] = React.useState<ParsedInvoice | null>(null);
  const [originalPdfUrl, setOriginalPdfUrl] = React.useState<string | null>(null);
  const originalPdfUrlRef = React.useRef<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [savedInvoices, setSavedInvoices] = React.useState<Array<Record<string, any>>>([]);
  const [listError, setListError] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState<Record<string, any> | null>(null);
  const [editLines, setEditLines] = React.useState<Array<Record<string, any>>>([]);
  const [editBusy, setEditBusy] = React.useState(false);
  const listRequestId = React.useRef(0);
  const editRequestId = React.useRef(0);
  const parseRequestId = React.useRef(0);
  const activeCompanyIdRef = React.useRef(companyId);
  const invoiceDraftKey = companyId ? `kartalyuvasi:besiktas-invoice-preview:${companyId}` : null;
  const invoiceDraftHydratedRef = React.useRef<string | null>(null);
  const invoiceDraftRestorePendingRef = React.useRef(false);

  // Restore the durable server-side working draft. This survives navigation,
  // deployment preview host changes and browser session resets.
  React.useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const requestedCompanyId = companyId;
    const db = createBrowserSupabase();
    if (!db.ok) return;
    invoiceDraftRestorePendingRef.current = true;
    void (async () => {
      try {
        const { data, error: draftError } = await db.client.rpc("get_supplier_invoice_working_draft", { p_company_id: requestedCompanyId });
        if (cancelled || activeCompanyIdRef.current !== requestedCompanyId) return;
        if (draftError) throw draftError;
        const draft = data as { invoice?: ParsedInvoice; test_only?: boolean } | null;
        if (draft?.invoice && Array.isArray(draft.invoice.lines) && draft.invoice.meta) {
          setInvoice(draft.invoice);
          if (typeof draft.test_only === "boolean") setTestOnly(draft.test_only);
        }
      } catch (e) {
        if (!cancelled) setError("Kayıtlı fatura çalışma taslağı yüklenemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
      } finally {
        if (!cancelled) {
          invoiceDraftHydratedRef.current = requestedCompanyId;
          invoiceDraftRestorePendingRef.current = false;
        }
      }
    })();
    return () => { cancelled = true; };
    // Hydrate only when company changes; adding invoice state would overwrite edits on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  React.useEffect(() => {
    if (!companyId || invoiceDraftHydratedRef.current !== companyId || invoiceDraftRestorePendingRef.current) return;
    const requestedCompanyId = companyId;
    const db = createBrowserSupabase();
    if (!db.ok) return;
    if (savedMessage) {
      void db.client.rpc("delete_supplier_invoice_working_draft", { p_company_id: requestedCompanyId })
        .then(({ error: deleteError }) => {
          if (deleteError && activeCompanyIdRef.current === requestedCompanyId) {
            setError("Fatura kaydedildi ancak çalışma taslağı temizlenemedi: " + deleteError.message);
          }
        });
      return;
    }
    if (!invoice) return;
    if (!invoice.file_hash || !invoice.file_name) {
      setError("Fatura çalışma taslağı için dosya özeti veya adı eksik; kalıcı taslak kaydedilmedi.");
      return;
    }
    let cancelled = false;
    void db.client.rpc("save_supplier_invoice_working_draft", {
      p_company_id: requestedCompanyId,
      p_file_sha256: invoice.file_hash,
      p_file_name: invoice.file_name,
      p_invoice_payload: invoice,
      p_test_only: testOnly,
    }).then(({ error: draftError }) => {
      if (cancelled || activeCompanyIdRef.current !== requestedCompanyId) return;
      if (draftError) setError("Fatura taslağı kalıcı olarak saklanamadı: " + draftError.message);
    });
    return () => { cancelled = true; };
  }, [companyId, invoice, testOnly, savedMessage]);

  const editSavedInvoice = async (row: Record<string, any>) => {
    const requestedCompanyId = companyId;
    const requestId = ++editRequestId.current;
    const db = createBrowserSupabase();
    if (!db.ok || !requestedCompanyId) return;
    setEditBusy(true); setListError("");
    try {
      const { data, error: queryError } = await db.client.from("supplier_invoice_lines")
        .select("id,line_number,source_product_code,product_name,quantity,unit_price,vat_rate,line_subtotal,line_vat,line_total,raw_data")
        .eq("company_id", requestedCompanyId).eq("invoice_id", row.id).order("line_number", { ascending: true });
      if (requestId !== editRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      if (queryError) throw queryError;
      setEditingId(String(row.id)); setEditDraft({ ...row });
      setEditLines((data ?? []).map((line: Record<string, any>) => ({ ...line })));
    } catch (e) {
      if (requestId === editRequestId.current && activeCompanyIdRef.current === requestedCompanyId) {
        setListError("Fatura açılamadı: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
      }
    } finally {
      if (requestId === editRequestId.current && activeCompanyIdRef.current === requestedCompanyId) setEditBusy(false);
    }
  };

  const saveEditedInvoice = async () => {
    const requestedCompanyId = companyId;
    if (!requestedCompanyId || !editingId || !editDraft || editLines.length === 0) return;
    const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
    const tolerance = 0.05;
    const subtotal = Number(editDraft.subtotal);
    const vatTotal = Number(editDraft.vat_total);
    const grandTotal = Number(editDraft.grand_total);
    if (!Number.isFinite(subtotal) || !Number.isFinite(vatTotal) || !Number.isFinite(grandTotal) ||
        subtotal < 0 || vatTotal < 0 || grandTotal < 0 || editDraft.totals_include_vat == null) {
      setListError("Fatura toplamları veya KDV durumu geçersiz. Değişiklikler kaydedilmedi."); return;
    }
    if (Math.abs(roundMoney(subtotal + vatTotal) - roundMoney(grandTotal)) > tolerance) {
      setListError("Ara toplam + KDV genel toplamla uyuşmuyor. Faturayı düzeltmeden kaydedemezsiniz."); return;
    }
    const invalidLine = editLines.some((line) => {
      const quantity = Number(line.quantity);
      const unitPrice = Number(line.unit_price);
      const rate = Number(line.vat_rate);
      const lineSubtotal = Number(line.line_subtotal ?? quantity * unitPrice);
      const lineVat = Number(line.line_vat ?? 0);
      const lineTotal = Number(line.line_total);
      return !String(line.product_name ?? "").trim() || !Number.isFinite(quantity) || quantity <= 0 ||
        !Number.isFinite(unitPrice) || unitPrice < 0 || !Number.isFinite(rate) || rate < 0 ||
        !Number.isFinite(lineSubtotal) || lineSubtotal < 0 || !Number.isFinite(lineVat) || lineVat < 0 ||
        !Number.isFinite(lineTotal) || lineTotal < 0;
    });
    if (invalidLine) {
      setListError("Fatura kalemlerinde eksik veya geçersiz alan var. Değişiklikler kaydedilmedi."); return;
    }
    const lineSubtotalSum = roundMoney(editLines.reduce((sum, line) => sum + Number(line.line_subtotal ?? Number(line.quantity) * Number(line.unit_price)), 0));
    if (Math.abs(lineSubtotalSum - roundMoney(subtotal)) > tolerance) {
      setListError("Fatura kalemleri toplamı ara toplamla uyuşmuyor. Değişiklikler kaydedilmedi."); return;
    }
    const lineVatValues = editLines.map((line) => line.line_vat);
    // The database RPC converts a null line VAT to zero. Never let an unknown VAT
    // silently become a real zero-tax amount during an edit.
    if (lineVatValues.some((value) => value == null || !Number.isFinite(Number(value)) || Number(value) < 0)) {
      setListError("En az bir kalemin KDV tutarı eksik veya geçersiz. KDV tutarlarını doğrulamadan kaydedemezsiniz."); return;
    }
    const lineVatSum = roundMoney(editLines.reduce((sum, line) => sum + Number(line.line_vat), 0));
    if (Math.abs(lineVatSum - roundMoney(vatTotal)) > tolerance) {
      setListError("Kalem KDV toplamı fatura KDV toplamıyla uyuşmuyor. Değişiklikler kaydedilmedi."); return;
    }
    const requestId = ++editRequestId.current;
    const db = createBrowserSupabase(); if (!db.ok) return;
    setEditBusy(true); setListError("");
    try {
      const { error: saveError } = await db.client.rpc("update_supplier_invoice_draft", {
        p_company_id: requestedCompanyId, p_invoice_id: editingId,
        p_invoice: {
          invoice_no: editDraft.invoice_no || null, invoice_date: editDraft.invoice_date || null,
          supplier_name: editDraft.supplier_name, supplier_tax_no: editDraft.supplier_tax_no || null,
          subtotal: Number(editDraft.subtotal), vat_total: Number(editDraft.vat_total),
          grand_total: Number(editDraft.grand_total), totals_include_vat: Boolean(editDraft.totals_include_vat),
        },
        p_lines: editLines.map((line, index) => ({
          line_number: index + 1, source_product_code: line.source_product_code || null,
          product_name: line.product_name, quantity: Number(line.quantity), unit_price: Number(line.unit_price),
          vat_rate: Number(line.vat_rate), line_subtotal: Number(line.line_subtotal ?? Number(line.quantity) * Number(line.unit_price)),
          line_vat: line.line_vat == null ? null : Number(line.line_vat), line_total: Number(line.line_total), raw_data: line.raw_data ?? {},
        })),
      });
      if (requestId !== editRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      if (saveError) throw saveError;
      setEditingId(null); setEditDraft(null); setEditLines([]);
      await loadSavedInvoices();
    } catch (e) {
      if (requestId === editRequestId.current && activeCompanyIdRef.current === requestedCompanyId) {
        setListError("Fatura güncellenemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
      }
    } finally {
      if (requestId === editRequestId.current && activeCompanyIdRef.current === requestedCompanyId) setEditBusy(false);
    }
  };
  const finalizeSavedInvoice = async (row: Record<string, any>) => {
    const requestedCompanyId = companyId;
    if (!requestedCompanyId || row.test_only || row.status === "finalized" ||
        !window.confirm(row.raw_metadata?.stock_exempt === true
          ? "Bu sarf malzeme faturasını stok kaydı oluşturmadan kesinleştirmek istiyor musunuz? Cari hesaba aktarım ayrı bir adımdır."
          : "Bu faturayı kesinleştirip stok hareket kayıtlarını oluşturmak istiyor musunuz? Bu işlem geri alınamaz; cari hesaba otomatik aktarım yapmaz.")) return;
    const db = createBrowserSupabase();
    if (!db.ok) { setListError("Supabase bağlantısı yapılandırılmamış."); return; }
    setEditBusy(true); setListError("");
    try {
      // Bring the latest durable product/variant choices into the saved invoice
      // draft before posting. The sync RPC only changes draft mappings; it never
      // posts inventory or cari movements. Finalization remains separately guarded.
      if (row.raw_metadata?.stock_exempt !== true) {
        const { data: syncData, error: syncError } = await db.client.rpc("sync_supplier_invoice_working_draft_matches", {
          p_company_id: requestedCompanyId, p_invoice_id: row.id,
        });
        if (syncError) throw syncError;
        if (syncData?.synced && (
          Number(syncData.skipped ?? 0) > 0 ||
          Number(syncData.applied ?? 0) !== Number(syncData.lines_synced ?? 0) ||
          Number(syncData.variant_pending ?? 0) > 0
        )) {
          throw new Error(`Ürün/varyant eşleşmeleri tamamlanmadı: ${Number(syncData.applied ?? 0)}/${Number(syncData.lines_synced ?? 0)} satır uygulandı, ${Number(syncData.variant_pending ?? 0)} satırda stok varyantı eksik. Stok kaydı oluşturulmadı; eşleşme ekranını kontrol edin.`);
        }
      }
      const { data, error: rpcError } = await db.client.rpc("finalize_supplier_invoice", {
        p_company_id: requestedCompanyId, p_invoice_id: row.id,
      });
      if (rpcError) throw rpcError;
      setListError("");
      await loadSavedInvoices();
      window.alert(data?.stock_exempt === true
        ? "Sarf malzeme faturası kesinleştirildi. Stok kaydı oluşturulmadı; Beşiktaş Cari aktarımı ayrı bir adımdır."
        : Number(data?.stock_lines_posted ?? 0) > 0
          ? "Fatura kesinleştirildi. Stok hareket defteri kayıtları oluşturuldu; mevcut stok snapshot raporu henüz güncellenmedi ve cari aktarımı yapılmadı."
          : "Fatura zaten kesinleştirilmiş. Yeni stok kaydı oluşturulmadı.");
    } catch (e) {
      setListError("Fatura kesinleştirilemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
    } finally { setEditBusy(false); }
  };

  const markInvoiceStockExempt = async (row: Record<string, any>) => {
    const requestedCompanyId = companyId;
    if (!requestedCompanyId || row.test_only || row.status !== "draft" || row.raw_metadata?.stock_exempt === true ||
        !window.confirm("Bu faturayı sarf malzeme / stok dışı olarak işaretlemek istiyor musunuz? Kesinleştirildiğinde stok hareketi oluşturulmaz; fatura toplamı cari hesaba ayrıca aktarılabilir.")) return;
    const db = createBrowserSupabase();
    if (!db.ok) { setListError("Supabase bağlantısı yapılandırılmamış."); return; }
    setEditBusy(true); setListError("");
    try {
      const { error: rpcError } = await db.client.rpc("set_supplier_invoice_stock_exempt", {
        p_company_id: requestedCompanyId, p_invoice_id: row.id, p_stock_exempt: true,
      });
      if (rpcError) throw rpcError;
      await loadSavedInvoices();
    } catch (e) {
      setListError("Sarf malzeme işareti kaydedilemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
    } finally { setEditBusy(false); }
  };

  const transferInvoiceToCari = async (row: Record<string, any>) => {
    const requestedCompanyId = companyId;
    if (!requestedCompanyId || row.test_only || row.status !== "finalized" || row.account_transferred_at ||
        !window.confirm("Bu faturanın genel toplamını Beşiktaş cari hesabına borç olarak aktarmak istiyor musunuz? Bu işlem stok miktarını değiştirmez.")) return;
    const db = createBrowserSupabase();
    if (!db.ok) { setListError("Supabase bağlantısı yapılandırılmamış."); return; }
    setEditBusy(true); setListError("");
    try {
      const { data, error: rpcError } = await db.client.rpc("transfer_supplier_invoice_to_current_account", {
        p_company_id: requestedCompanyId, p_invoice_id: row.id,
      });
      if (rpcError) throw rpcError;
      await loadSavedInvoices();
      window.alert(data?.already_transferred
        ? "Bu fatura daha önce cariye aktarılmış; ikinci hareket oluşturulmadı."
        : "Fatura Beşiktaş cari hesabına aktarıldı.");
    } catch (e) {
      setListError("Cariye aktarma başarısız: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
    } finally { setEditBusy(false); }
  };

  const loadSavedInvoices = React.useCallback(async () => {
    const requestedCompanyId = companyId;
    const requestId = ++listRequestId.current;
    if (!requestedCompanyId) { setSavedInvoices([]); setListError(""); return; }
    const db = createBrowserSupabase();
    if (!db.ok) { setListError("Supabase bağlantısı yapılandırılmamış."); return; }
    setListError("");
    const {data,error:e}=await db.client.from("supplier_invoices").select("id,invoice_no,invoice_date,supplier_name,supplier_tax_no,subtotal,vat_total,grand_total,totals_include_vat,status,test_only,file_name,raw_metadata,created_at,finalized_at,account_transferred_at").eq("company_id",requestedCompanyId).order("created_at",{ascending:false}).limit(100);
    if (requestId !== listRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
    if(e) { setSavedInvoices([]); setListError("Kayıtlar yüklenemedi: "+e.message); } else {setListError("");setSavedInvoices((data??[]) as Array<Record<string,any>>);}
  },[companyId]);
  React.useLayoutEffect(()=>{
    activeCompanyIdRef.current = companyId;
    listRequestId.current += 1;
    editRequestId.current += 1;
    parseRequestId.current += 1;
    setSavedInvoices([]);
    setListError("");
    setEditingId(null); setEditDraft(null); setEditLines([]);
    setInvoice(null); setError(""); setSavedMessage(""); setLoading(false);
    void loadSavedInvoices();
    return () => { listRequestId.current += 1; editRequestId.current += 1; };
  },[companyId,loadSavedInvoices]);
  const deleteSavedInvoice = async (row: Record<string, any>) => {
    const requestedCompanyId = companyId;
    if (!requestedCompanyId || row.test_only || row.status !== "draft" || editBusy) return;
    if (!window.confirm(`${row.invoice_no || row.file_name || "Bu fatura"} taslağı ve tüm kalemleri kalıcı olarak silinsin mi? Stok ve cari hareketleri değiştirilmeyecek.`)) return;
    const db = createBrowserSupabase();
    if (!db.ok) { setListError("Supabase bağlantısı yapılandırılmamış."); return; }
    setEditBusy(true);
    setListError("");
    try {
      const { error: deleteError } = await db.client.rpc("delete_supplier_invoice_draft", {
        p_company_id: requestedCompanyId,
        p_invoice_id: row.id,
      });
      if (activeCompanyIdRef.current !== requestedCompanyId) return;
      if (deleteError) throw deleteError;
      await loadSavedInvoices();
    } catch (e) {
      if (activeCompanyIdRef.current === requestedCompanyId) {
        setListError("Fatura silinemedi: " + ((e as { message?: string }).message ?? "Bilinmeyen hata"));
      }
    } finally {
      if (activeCompanyIdRef.current === requestedCompanyId) setEditBusy(false);
    }
  };

  React.useEffect(() => () => {
    if (originalPdfUrlRef.current) URL.revokeObjectURL(originalPdfUrlRef.current);
  }, []);

  const chooseFile = async (file?: File, referenceOnly = false) => {
    if (!file) return;
    const lower = file.name.toLocaleLowerCase("tr-TR");
    if (file.size > 25 * 1024 * 1024) { setError("Dosya 25 MB sınırını aşıyor."); return; }
    const isPdf = lower.endsWith(".pdf");
    const isXml = lower.endsWith(".xml");
    if (!isPdf && !isXml) { setError("Lütfen e-Arşiv PDF veya XML dosyası yükleyin."); return; }
    if (isPdf) {
      if (originalPdfUrlRef.current) URL.revokeObjectURL(originalPdfUrlRef.current);
      originalPdfUrlRef.current = URL.createObjectURL(file);
      setOriginalPdfUrl(originalPdfUrlRef.current);
      if (referenceOnly) { setError(""); return; }
    }
    const requestedCompanyId = companyId;
    const requestId = ++parseRequestId.current;
    if (!requestedCompanyId) { setError("Önce bir şirket seçin."); return; }
    setError(""); setSavedMessage(""); setInvoice(null); setLoading(true);
    try {
      const parsed = isPdf ? await parseBesiktasPdf(file) : await parseBesiktasXml(file);
      if (requestId === parseRequestId.current && activeCompanyIdRef.current === requestedCompanyId) setInvoice(parsed);
    } catch (e) {
      if (requestId === parseRequestId.current && activeCompanyIdRef.current === requestedCompanyId) {
        setError(e instanceof Error ? "Dosya okunamadı: " + e.message : "Dosya okunurken beklenmeyen bir hata oluştu.");
      }
    } finally {
      if (requestId === parseRequestId.current && activeCompanyIdRef.current === requestedCompanyId) setLoading(false);
    }
  };

  const saveInvoice = async () => {
    if (!invoice || !companyId || !user) { setError("Oturum veya şirket üyeliği bulunamadı."); return; }
    if (invoice.meta.invoice_date != null && !isValidIsoDate(invoice.meta.invoice_date)) {
      setError("Fatura tarihi geçerli bir takvim tarihi değil. Tarih alanını düzeltin veya boş bırakın; geçersiz tarih kaydedilmedi.");
      return;
    }
    if (!invoice.meta.supplier_name || invoice.meta.subtotal == null || invoice.meta.vat_total == null ||
        invoice.meta.grand_total == null || invoice.meta.totals_include_vat == null) {
      setError("Tedarikçi, ara toplam, KDV, genel toplam ve KDV durumu belirlenmeden kayıt yapılamaz."); return;
    }
    if (!invoice.lines.length || invoice.lines.some((line) =>
      !line.product_name || line.qty == null || line.qty <= 0 || line.unit_price == null ||
      line.unit_price < 0 || line.vat_rate == null || !isValidVatAmount(line.vat_amount) ||
      line.line_total == null || line.line_total < 0)) {
      setError("Kalemlerde eksik veya hatalı zorunlu alan var. Her kalemin KDV tutarı dahil tüm alanları doğrulanmadan kayıt yapılmadı."); return;
    }
    // Reject invoices whose header totals or parsed line totals contradict one another.
    // Do not silently save a partial/misread invoice as a valid draft.
    const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
    const tolerance = 0.05;
    const { subtotal, vat_total, grand_total, totals_include_vat } = invoice.meta;
    if (subtotal == null || vat_total == null || grand_total == null || totals_include_vat == null) {
      setError("Fatura toplamları veya KDV durumu doğrulanamadı. Orijinal PDF ile alanları kontrol edin; kayıt yapılmadı."); return;
    }
    if (Math.abs(roundMoney(subtotal + vat_total) - roundMoney(grand_total)) > tolerance) {
      setError("Fatura ara toplamı + KDV, genel toplamla uyuşmuyor. Orijinal belgeyi kontrol edin; kayıt yapılmadı."); return;
    }
    const parsedLineTotal = roundMoney(invoice.lines.reduce((sum, line) => sum + (line.line_total ?? 0), 0));
    const expectedLineTotal = totals_include_vat ? grand_total : subtotal;
    if (Math.abs(parsedLineTotal - roundMoney(expectedLineTotal)) > tolerance) {
      setError(totals_include_vat
        ? "Okunan satır toplamları KDV dâhil genel toplamla uyuşmuyor. Eksik/yanlış okunan kalemleri düzeltmeden kayıt yapılamaz."
        : "Okunan satır toplamları KDV hariç ara toplamla uyuşmuyor. Eksik/yanlış okunan kalemleri düzeltmeden kayıt yapılamaz."); return;
    }
    if (invoice.lines.every((line) => line.vat_amount != null)) {
      const parsedVatTotal = roundMoney(invoice.lines.reduce((sum, line) => sum + (line.vat_amount ?? 0), 0));
      if (Math.abs(parsedVatTotal - roundMoney(vat_total)) > tolerance) {
        setError("Satır KDV toplamları fatura KDV toplamıyla uyuşmuyor. Kayıt yapılmadı."); return;
      }
    }
    const factory = createBrowserSupabase();
    if (!factory.ok) { setError("Supabase bağlantısı yapılandırılmamış."); return; }
    setSaving(true); setError(""); setSavedMessage("");
    try {
      const { data: savedInvoiceId, error: rpcError } = await factory.client.rpc("save_supplier_invoice_draft", {
        p_company_id: companyId, p_test_only: testOnly,
        p_file_name: invoice.file_name, p_file_sha256: invoice.file_hash,
        p_invoice: {
          invoice_no: invoice.meta.invoice_no, invoice_date: invoice.meta.invoice_date,
          supplier_name: invoice.meta.supplier_name, supplier_tax_no: invoice.meta.supplier_tax_no,
          currency_code: "TRY", subtotal: invoice.meta.subtotal, vat_total: invoice.meta.vat_total,
          grand_total: invoice.meta.grand_total, totals_include_vat: invoice.meta.totals_include_vat,
          raw_metadata: { source: invoice.source, issues: invoice.issues, stock_exempt: (invoice as ParsedInvoice & { stock_exempt?: boolean }).stock_exempt === true }
        },
        p_lines: invoice.lines.map((line) => ({
          line_number: line.line_no, source_product_code: line.product_code, product_name: line.product_name,
          source_color_code: null, source_color_name: line.color_name ?? null, source_size_code: line.size ?? null,
          quantity: line.qty, unit_price: line.unit_price, vat_rate: line.vat_rate,
          line_subtotal: invoice.source === "xml" ? line.line_total : Math.round((line.qty! * line.unit_price!) * 100) / 100, line_vat: line.vat_amount, line_total: line.line_total,
          mapping_status: "unmatched", raw_data: { unit: line.unit, color_name: line.color_name ?? null, size: line.size ?? null, issues: line.issues, source_match: line.match, matched_product_code: (line as any).matched_product_code ?? null, matched_product_variant_id: (line as any).matched_product_variant_id ?? null }
        }))
      });
      if (rpcError) throw rpcError;
      let mappingMessage = "";
      if (savedInvoiceId && invoice.lines.some((line) => (line as any).matched_product_code)) {
        const { data: mappingResult, error: mappingError } = await factory.client.rpc("apply_supplier_invoice_line_mappings", {
          p_company_id: companyId, p_invoice_id: savedInvoiceId,
        });
        if (mappingError) {
          mappingMessage = " Fatura kaydedildi ancak ürün eşleşmeleri uygulanamadı; veritabanı migration kontrol edilmeli.";
        } else {
          const applied = Number(mappingResult?.applied ?? 0);
          const skipped = Number(mappingResult?.skipped ?? 0);
          mappingMessage = ` Ürün eşleşmesi: ${applied} uygulandı, ${skipped} belirsiz kalem beklemede.`;
        }
      }
      setSavedMessage((testOnly ? "TEST fatura taslağı kaydedildi." : "Gerçek İFM faturası taslak olarak kaydedildi.") + mappingMessage + " Stok ve cari hareket oluşturulmadı.");
      if (!testOnly) {
        setSavedMessage((current) => current + " Yapay zekâ veri denetimi başlatıldı…");
        void auditCompanyDataAfterChange(companyId, "Beşiktaş fatura taslağı").then((audit) => {
          setSavedMessage((current) => current.replace(" Yapay zekâ veri denetimi başlatıldı…", "") +
            (audit.ok ? " AI denetimi: " + audit.text.slice(0, 1200) : " AI denetimi tamamlanamadı: " + audit.message));
        }).catch((auditError) => {
          setSavedMessage((current) => current.replace(" Yapay zekâ veri denetimi başlatıldı…", "") +
            " AI denetimi başlatılamadı: " + (auditError instanceof Error ? auditError.message : "Beklenmeyen hata."));
        });
      }
    } catch (e) {
      // Supabase/PostgREST hataları her zaman Error örneği değildir.
      // message alanını okumazsak asıl veritabanı hatası gizlenir.
      const err = e as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown };
      const parts = [
        typeof err?.message === "string" ? err.message : null,
        typeof err?.details === "string" ? err.details : null,
        typeof err?.hint === "string" ? err.hint : null,
        typeof err?.code === "string" ? `Kod: ${err.code}` : null,
      ].filter(Boolean);
      setError(parts.length ? `Fatura kaydedilemedi: ${parts.join(" — ")}` :
        e instanceof Error ? `Fatura kaydedilemedi: ${e.message}` : "Fatura kaydedilemedi: ayrıntı alınamadı.");
    }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="page-besiktas" className="space-y-6">
      <PageHeader
        eyebrow="Veri İşleme"
        title="Beşiktaş Faturaları"
        description="PDF veya XML faturayı yükleyin; okunan bilgileri ve ürün kalemlerini kontrol edip düzenleyerek taslak kaydedin."
      />

      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
        <strong>Fatura taslağı:</strong> Fatura ve kalemleri birlikte taslak olarak kaydedilir.
        Bu işlem stok, cari hareket veya ürün eşleştirmesi oluşturmaz.
      </div>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-dashed border-border bg-card p-6 md:col-span-2">
                    <div className="flex flex-col items-center text-center">
            <UploadCloud className="mb-3 h-9 w-9 text-muted-foreground" />
            <h2 className="font-serif text-xl">Fatura PDF veya XML yükle</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              PDF metni otomatik okunur; alanları kontrol edip düzeltebilirsiniz. XML de desteklenir · En fazla 25 MB
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".xml,.pdf,application/xml,text/xml,application/pdf"
              className="mt-4 block w-full max-w-sm text-sm"
              disabled={loading}
              onChange={(event) => {
                void chooseFile(event.target.files?.[0]);
                event.currentTarget.value = "";
              }}
            />
                        {loading && (
              <p className="mt-3 text-sm" role="status">
                Dosya okunuyor ve önizleme hazırlanıyor…
              </p>
            )}
            {error && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>

        </div>

        <aside className="rounded-lg border border-border bg-card p-5">
          <h3 className="font-serif text-lg">İşlem adımları</h3>
          <ol className="mt-3 list-inside list-decimal space-y-2 text-sm text-muted-foreground">
            <li>Dosyayı seçin.</li>
            <li>Fatura bilgilerini kontrol edin.</li>
            <li>Kalemleri ve uyarıları inceleyin.</li>
            <li>Fatura taslağını kaydedin.</li>
          </ol>
        </aside>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-3"><div><h2 className="font-serif text-xl">Kayıtlı fatura taslakları</h2><p className="text-sm text-muted-foreground">Test ve gerçek İFM taslakları; taslak kayıt stok veya cari hareket oluşturmaz.</p></div><Button variant="outline" onClick={() => void loadSavedInvoices()}>Yenile</Button></div>
        {listError && <p role="alert" className="text-sm text-destructive">{listError}</p>}
        {savedInvoices.length === 0 && <p className="text-sm text-muted-foreground">Kayıtlı fatura taslağı bulunamadı.</p>}
        {savedInvoices.length > 0 && <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr><th className="p-2">Fatura</th><th className="p-2">Tarih</th><th className="p-2">Tedarikçi</th><th className="p-2">Tutar</th><th className="p-2">İşlem</th></tr></thead><tbody>{savedInvoices.map(row=><tr key={row.id} className="border-t border-border"><td className="p-2"><div>{row.invoice_no||row.file_name||"—"}</div><span className="text-xs text-muted-foreground">{row.test_only ? "TEST" : "GERÇEK İFM"}{row.raw_metadata?.stock_exempt === true ? " · SARF MALZEME / STOK DIŞI" : ""}</span></td><td className="p-2">{row.invoice_date||"—"}</td><td className="p-2">{row.supplier_name||"—"}</td><td className="p-2">{money(Number(row.grand_total))}</td><td className="p-2"><div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" disabled={row.status!=="draft"||editBusy} onClick={()=>void editSavedInvoice(row)}>Düzenle</Button>{row.status==="draft" && row.raw_metadata?.stock_exempt !== true ? <Button size="sm" variant="outline" disabled={row.test_only||editBusy} onClick={()=>void markInvoiceStockExempt(row)}>Sarf malzeme</Button> : null}<Button size="sm" disabled={row.test_only||!["draft","ready_to_post"].includes(row.status)||editBusy} onClick={()=>void finalizeSavedInvoice(row)}>{row.raw_metadata?.stock_exempt === true ? "Stoksuz kesinleştir" : "Kesinleştir"}</Button><Button size="sm" variant="outline" disabled={row.test_only||row.status!=="finalized"||Boolean(row.account_transferred_at)||editBusy} onClick={()=>void transferInvoiceToCari(row)}>Cariye aktar</Button>{!row.test_only && row.status === "draft" ? <Button size="sm" variant="destructive" disabled={editBusy} onClick={()=>void deleteSavedInvoice(row)}>Taslağı sil</Button> : null}</div><div className="mt-1 text-xs text-muted-foreground">{row.status==="finalized" ? (row.account_transferred_at ? "Kesinleşti · Cariye aktarıldı" : "Kesinleşti · Cari aktarımı bekliyor") : row.status==="void" ? "İptal" : "Taslak"}</div></td></tr>)}</tbody></table></div>}
      </section>

      {editingId && editDraft && (
        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <h2 className="font-serif text-xl">Fatura taslağını düzenle</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {([["invoice_no","Fatura numarası"],["invoice_date","Fatura tarihi"],["supplier_name","Tedarikçi"],["supplier_tax_no","Vergi numarası"],["subtotal","Ara toplam"],["vat_total","KDV"],["grand_total","Genel toplam"]] as const).map(([key,label]) => (
              <label key={key} className="space-y-1 text-sm"><span>{label}</span>
                <input type={key === "invoice_date" ? "date" : ["subtotal","vat_total","grand_total"].includes(key) ? "number" : "text"} step="0.01" value={editDraft[key] ?? ""} onChange={event => setEditDraft(previous => previous ? {...previous,[key]:event.target.value} : previous)} className="w-full rounded-md border border-border bg-background px-3 py-2" />
              </label>
            ))}
          </div>
          <h3 className="font-medium">Fatura kalemleri</h3>
          {editLines.map((line,index) => (
            <div key={line.id ?? index} className="grid gap-2 rounded border border-border p-3 sm:grid-cols-2 lg:grid-cols-3">
              {([["product_name","Ürün adı"],["source_product_code","Ürün kodu"],["quantity","Miktar"],["unit_price","Birim fiyat"],["vat_rate","KDV %"],["line_total","Satır toplamı"]] as const).map(([key,label]) => (
                <label key={key} className="space-y-1 text-sm"><span>{label}</span>
                  <input type={["quantity","unit_price","vat_rate","line_total"].includes(key) ? "number" : "text"} step="0.01" value={line[key] ?? ""} onChange={event => setEditLines(previous => previous.map((item,index2) => index2 === index ? {...item,[key]:event.target.value} : item))} className="w-full rounded-md border border-border bg-background px-3 py-2" />
                </label>
              ))}
            </div>
          ))}
          <div className="flex gap-2"><Button onClick={() => void saveEditedInvoice()} disabled={editBusy}>{editBusy ? "Kaydediliyor…" : "Değişiklikleri kaydet"}</Button><Button variant="outline" onClick={() => {setEditingId(null);setEditDraft(null);setEditLines([]);}}>Vazgeç</Button></div>
        </section>
      )}
      {invoice && (
        <section className="space-y-5 rounded-lg border border-border bg-card p-5">
          {originalPdfUrl && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <div>
                <h2 className="font-serif text-xl">Orijinal fatura görüntüsü</h2>
                <p className="text-sm text-muted-foreground">Yüklediğiniz PDF'nin orijinal görüntüsü. Okunan alanları ve kalemleri bu belgeyle karşılaştırıp gerektiğinde düzeltin.</p>
              </div>
              <iframe title="Orijinal Beşiktaş fatura PDF" src={originalPdfUrl} className="h-[75vh] min-h-[560px] w-full rounded border border-border bg-white" />
              <a href={originalPdfUrl} target="_blank" rel="noreferrer" className="text-sm underline">Orijinal PDF’yi ayrı sekmede aç</a>
            </div>
          )}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-serif text-xl">Fatura önizlemesi</h2>
              <p className="mt-1 break-all text-sm text-muted-foreground">
                {invoice.file_name}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded bg-secondary px-2 py-1">
                {invoice.source.toUpperCase()}
              </span>
            </div>
          </div>

          <section id="invoice-editable-fields" className="scroll-mt-6 rounded-md border border-border p-4">
            <h3 className="mb-3 font-medium">Fatura bilgileri (düzenlenebilir)</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {(["invoice_no","invoice_date","supplier_name","supplier_tax_no","subtotal","vat_total","grand_total"] as const).map((key) => (
                <label key={key} className="space-y-1 text-sm">
                  <span>{{ invoice_no: "Fatura numarası", invoice_date: "Fatura tarihi", supplier_name: "Tedarikçi", supplier_tax_no: "Vergi numarası", subtotal: "Ara toplam", vat_total: "KDV toplamı", grand_total: "Genel toplam" }[key]}</span>
                  <input type={key === "invoice_date" ? "date" : ["subtotal","vat_total","grand_total"].includes(key) ? "number" : "text"}
                    step={["subtotal","vat_total","grand_total"].includes(key) ? "0.01" : undefined}
                    value={invoice.meta[key] ?? ""}
                    onChange={event => { const raw = event.target.value; setInvoice(previous => previous ? { ...previous, error_count: 0, meta: { ...previous.meta, [key]: ["subtotal","vat_total","grand_total"].includes(key) ? (raw === "" ? null : Number(raw)) : (raw || null) } } : previous); setError(""); setSavedMessage(""); }}
                    className="w-full rounded-md border border-border bg-background px-3 py-2" />
                </label>
              ))}
              <label className="space-y-1 text-sm"><span>Tutarların KDV durumu</span>
                <select value={invoice.meta.totals_include_vat == null ? "" : String(invoice.meta.totals_include_vat)}
                  onChange={event => { const value = event.target.value; setInvoice(previous => previous ? { ...previous, error_count: 0, meta: { ...previous.meta, totals_include_vat: value === "" ? null : value === "true" } } : previous); setError(""); setSavedMessage(""); }}
                  className="w-full rounded-md border border-border bg-background px-3 py-2">
                  <option value="">Belirlenemedi</option><option value="true">KDV dahil</option><option value="false">KDV hariç</option>
                </select>
              </label>
            </div>
          </section><div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["Fatura numarası", invoice.meta.invoice_no],
              ["Fatura tarihi", invoice.meta.invoice_date],
              ["Tedarikçi", invoice.meta.supplier_name],
              ["Vergi numarası", invoice.meta.supplier_tax_no],
              ["Mal/hizmet toplamı", money(invoice.meta.subtotal)],
              ["KDV toplamı", money(invoice.meta.vat_total)],
              ["Genel toplam", money(invoice.meta.grand_total)],
              [
                "Tutarların KDV durumu",
                invoice.meta.totals_include_vat == null
                  ? "Belirlenemedi"
                  : invoice.meta.totals_include_vat
                    ? "KDV dahil"
                    : "KDV hariç",
              ],
            ].map(([label, value]) => (
              <div key={label} className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">{label}</p>
                {label === "Fatura tarihi" ? (
                  <div className="mt-1 space-y-1">
                    <input
                      aria-label="Fatura tarihi"
                      type="date"
                      value={isValidIsoDate(invoice.meta.invoice_date) ? invoice.meta.invoice_date : ""}
                      onChange={(event) => {
                        const nextDate = event.target.value || null;
                        setInvoice((previous) => previous
                          ? { ...previous, meta: { ...previous.meta, invoice_date: nextDate } }
                          : previous);
                        setError("");
                        setSavedMessage("");
                      }}
                      className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                    />
                    {invoice.meta.invoice_date && !isValidIsoDate(invoice.meta.invoice_date) ? (
                      <p className="text-xs text-amber-700">
                        Okunan tarih geçersiz: {invoice.meta.invoice_date}. Kaydetmeden önce düzeltin veya boş bırakın.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-1 break-words text-sm font-medium">{value || "—"}</p>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-md bg-secondary/60 p-3">
              <p className="text-xs text-muted-foreground">Kalem sayısı</p>
              <p className="text-xl font-semibold">{invoice.lines.length}</p>
            </div>
            <div className="rounded-md bg-secondary/60 p-3">
              <p className="text-xs text-muted-foreground">Hata</p>
              <p className="text-xl font-semibold">{invoice.error_count}</p>
            </div>
            <div className="rounded-md bg-secondary/60 p-3">
              <p className="text-xs text-muted-foreground">Uyarı</p>
              <p className="text-xl font-semibold">{invoice.warn_count}</p>
            </div>
            <div className="rounded-md bg-secondary/60 p-3">
              <p className="text-xs text-muted-foreground">Dosya özeti</p>
              <p className="break-all text-xs">
                {invoice.file_hash.slice(0, 16)}…
              </p>
            </div>
          </div>

          {invoice.issues.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium">Dosya uyarıları</h3>
              {invoice.issues.map((issue, index) => (
                <div
                  key={`${issue.code}-${index}`}
                  className="flex gap-2 rounded-md border border-amber-200 p-3 text-sm"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                  <span>{issue.message}</span>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <label className="flex items-start gap-3"><input type="checkbox" checked={(invoice as ParsedInvoice & { stock_exempt?: boolean }).stock_exempt === true} onChange={(event) => setInvoice(previous => previous ? { ...previous, stock_exempt: event.target.checked } as ParsedInvoice : previous)} className="mt-1" /><span><strong>Sarf malzeme / stok dışı fatura.</strong><span className="mt-1 block">Bu seçeneği yalnızca müşteriye satılmayan, stokta izlenmeyecek gider malzemeleri için işaretleyin. Kesinleştirildiğinde ürün eşleştirmesi ve stok hareketi oluşturulmaz; fatura tutarı Beşiktaş Cari hesabına ayrıca aktarılır.</span></span></label>
          </div>

          <div>
            <h3 className="mb-3 font-serif text-lg">Fatura kalemleri</h3>
            <Button variant="outline" className="mb-3" onClick={() => setInvoice(previous => previous ? { ...previous, error_count: 0, lines: [...previous.lines, { line_no: previous.lines.length + 1, product_code: null, product_name: "", color_name: null, size: null, qty: null, unit: "Adet", unit_price: null, vat_rate: 20, vat_amount: null, line_total: null, match: "unknown", issues: [] }] } : previous)}>+ Fatura kalemi ekle</Button>
            {invoice.lines.length === 0 ? (
              <div className="rounded-md border border-border p-5 text-sm text-muted-foreground">
                <AlertTriangle className="mr-2 inline h-4 w-4" />
                Kalem çıkarılamadı. PDF metin içermeyen taranmış bir belge olabilir
                veya Excel sütun başlıkları tanınmamış olabilir.
                {invoice.raw_text && (
                  <details className="mt-3">
                    <summary className="cursor-pointer font-medium">
                      PDF ham metnini göster
                    </summary>
                    <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap text-xs">
                      {invoice.raw_text}
                    </pre>
                  </details>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full min-w-[850px] text-left text-sm">
                  <thead className="bg-secondary/60">
                    <tr>
                      {["#", "Ürün kodu", "Ürün adı", "Renk", "Beden", "Miktar", "Birim fiyat", "KDV %", "KDV tutarı", "Satır toplamı", "Durum"].map((h) => (
                        <th key={h} className="p-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lines.map((line, index) => (
                      <tr key={`${line.line_no}-${index}`} className="border-t border-border align-top">
                        <td className="p-3">{line.line_no}</td>
                        <td className="p-3"><input aria-label="Ürün kodu" value={line.product_code ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,product_code:e.target.value||null}:x)}:p)} className="w-28 rounded border bg-background px-2 py-1" /></td>
                        <td className="max-w-xs whitespace-normal p-3"><input aria-label="Ürün adı" value={line.product_name ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,product_name:e.target.value||null}:x)}:p)} className="w-64 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3"><input aria-label="Renk" value={line.color_name ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,color_name:e.target.value||null}:x)}:p)} className="w-24 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3"><input aria-label="Beden" value={line.size ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,size:e.target.value||null}:x)}:p)} className="w-20 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3"><input aria-label="Miktar" type="number" step="0.001" value={line.qty ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,qty:e.target.value===""?null:Number(e.target.value)}:x)}:p)} className="w-20 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3"><input aria-label="Birim fiyat" type="number" step="0.0001" value={line.unit_price ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,unit_price:e.target.value===""?null:Number(e.target.value)}:x)}:p)} className="w-28 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3"><input aria-label="KDV oranı" type="number" step="0.01" value={line.vat_rate ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,vat_rate:e.target.value===""?null:Number(e.target.value)}:x)}:p)} className="w-20 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3"><input aria-label="KDV tutarı" type="number" step="0.01" value={line.vat_amount ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,vat_amount:e.target.value===""?null:Number(e.target.value)}:x)}:p)} className="w-28 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3"><input aria-label="Satır toplamı" type="number" step="0.01" value={line.line_total ?? ""} onChange={e => setInvoice(p => p ? {...p,error_count:0,lines:p.lines.map((x,i)=>i===index?{...x,line_total:e.target.value===""?null:Number(e.target.value)}:x)}:p)} className="w-28 rounded border bg-background px-2 py-1" /></td>
                        <td className="p-3">
                          {line.issues.length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-green-700">
                              <CheckCircle2 className="h-4 w-4" /> Uyarı yok
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <AlertTriangle className="h-4 w-4" /> {line.issues.length} uyarı
                            </span>
                          )}
                          {line.issues.length > 0 && (
                            <ul className="mt-1 max-w-xs space-y-1 text-xs text-muted-foreground">
                              {line.issues.map((issue, i) => (
                                <li key={`${issue.code}-${i}`}>{issue.message}</li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <strong>Taslak kayıt.</strong> Fatura ve kalemleri birlikte kaydedilir; stok, cari hareket ve ürün eşleştirmesi oluşturulmaz.
          </div>

          {savedMessage && <p role="status" className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900">{savedMessage}</p>}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => {
              const editor = document.getElementById("invoice-editable-fields");
              editor?.scrollIntoView({ behavior: "smooth", block: "start" });
              (editor?.querySelector("input") as HTMLInputElement | null)?.focus();
            }}>
              Düzenle
            </Button>
            <Button onClick={() => void saveInvoice()} disabled={saving || Boolean(savedMessage) || !companyId || !user || invoice.lines.length === 0}>
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
            <Button variant="outline" disabled={editBusy} onClick={() => {
              const requestedCompanyId = companyId;
              const clearPreview = async () => {
                // Clear the durable server-side working draft too; otherwise it is restored on refresh.
                setInvoice(null); setError(""); setSavedMessage("");
                if (inputRef.current) inputRef.current.value = "";
                if (originalPdfUrlRef.current) URL.revokeObjectURL(originalPdfUrlRef.current);
                originalPdfUrlRef.current = null; setOriginalPdfUrl(null);
                if (!requestedCompanyId) return;
                const db = createBrowserSupabase();
                if (!db.ok) { setError("Supabase bağlantısı yapılandırılmamış; kalıcı taslak temizlenemedi."); return; }
                const { error: deleteError } = await db.client.rpc("delete_supplier_invoice_working_draft", { p_company_id: requestedCompanyId });
                if (deleteError) setError("Önizleme ekrandan temizlendi ancak kalıcı çalışma taslağı silinemedi: " + deleteError.message);
              };
              void clearPreview();
            }}>
              Önizlemeyi temizle
            </Button>
            <Button variant="outline" onClick={() => inputRef.current?.click()}>
              <FileText className="mr-2 h-4 w-4" />
              Başka dosya seç
            </Button>
          </div>
        </section>
      )}

      {!invoice && !loading && !error && (
        <div className="rounded-lg border border-border p-5 text-sm text-muted-foreground">
          <Receipt className="mr-2 inline h-4 w-4" />
          Henüz önizlenen fatura yok. Dosya seçtiğinizde sonuçlar bu ekranda gösterilir.
        </div>
      )}
    </div>
  );
};

export default BesiktasInvoices;
